import axios from 'axios'
import {
  createUserWithEmailAndPassword,
  getAuth,
  signInWithEmailAndPassword,
} from 'firebase/auth'
import {
  collection,
  doc,
  getDocs,
  getFirestore,
  query as firebaseQuery,
  setDoc,
  where,
} from 'firebase/firestore'
import {AuthModel, UserModel} from './_models'
import {withTimeout} from './session'

const API_URL = process.env.REACT_APP_API_URL

export const GET_USER_BY_ACCESSTOKEN_URL = `${API_URL}/verify_token`
export const LOGIN_URL = `${API_URL}/login`
export const REGISTER_URL = `${API_URL}/register`
export const REQUEST_PASSWORD_URL = `${API_URL}/forgot_password`

const toAuthPayload = async (uid: string, refreshToken: string): Promise<AuthModel> => {
  return {
    api_token: uid,
    refreshToken,
  }
}

const mapFirestoreUser = (user: Record<string, any>): UserModel => {
  return {
    id: user.uid,
    username: user.name ? user.name : 'Guest',
    password: user.password,
    email: user.email,
    first_name: user.first_name ? user.first_name : 'Guest',
    last_name: user.last_name ? user.last_name : 'Guest',
    fullname: user.fullname ? user.fullname : 'Guest',
    occupation: user.occupation ? user.occupation : 'N/A',
    companyName: user.companyName ? user.companyName : 'N/A',
    phone: user.companyName ? user.companyName : 'N/A',
    roles: [0],
    pic: user.image ? user.image : 'N/A',
    language: 'en',
    timeZone: user.timeZone ? user.timeZone : 'N/A',
    website: user.website ? user.website : 'N/A',
    emailSettings: {
      emailNotification: true,
      sendCopyToPersonalEmail: true,
      activityRelatesEmail: {
        youHaveNewNotifications: true,
        youAreSentADirectMessage: true,
        someoneAddsYouAsAsAConnection: true,
        uponNewOrder: true,
        newMembershipApproval: true,
        memberRegistration: true,
      },
      updatesFromKeenthemes: {
        newsAboutKeenthemesProductsAndFeatureUpdates: true,
        tipsOnGettingMoreOutOfKeen: true,
        thingsYouMissedSindeYouLastLoggedIntoKeen: true,
        newsAboutStartOnPartnerProductsAndOtherServices: true,
        tipsOnStartBusinessProducts: true,
      },
    },
    auth: {
      api_token: user.uid,
      refreshToken: user.refreshToken,
    },
    communication: {
      email: true,
      sms: true,
      phone: true,
    },
    address: {
      addressLine: 'N/A',
      city: 'N/A',
      postCode: 'N/A',
      state: 'N/A',
    },
    socialNetworks: {
      linkedIn: 'https://linkedin.com',
      facebook: 'https://facebook.com',
      twitter: 'https://linkedin.com',
      instagram: 'https://linkedin.com',
    },
  } as UserModel
}

export async function login(email: string, password: string): Promise<{data: AuthModel}> {
  const auth = getAuth()
  const response = await signInWithEmailAndPassword(auth, email, password)
  const payload = await toAuthPayload(response.user.uid, response.user.refreshToken)
  return {data: payload}
}

export async function createPassword(email: string, password: string): Promise<{data: AuthModel}> {
  return login(email, password)
}

export async function register(
  email: string,
  firstName: string,
  prefix: string,
  lastName: string,
  password: string,
  _passwordConfirmation: string
): Promise<{data?: AuthModel; error?: string}> {
  const auth = getAuth()
  const db = getFirestore()
  const usersQuery = firebaseQuery(collection(db, '_users'), where('email', '==', email))

  try {
    const querySnapshot = await getDocs(usersQuery)

    if (!querySnapshot.size) {
      return {error: 'Invitation does not exist.'}
    }

    const document = querySnapshot.docs[0]
    const invitedUser = document.data()

    if (invitedUser.password) {
      return {error: 'Unauthorized attempt.'}
    }

    const panelUser = doc(db, '_users', invitedUser.id)
    const response = await createUserWithEmailAndPassword(auth, email, password)

    await setDoc(
      panelUser,
      {
        password,
        uid: response.user.uid,
        name: firstName + ' ' + prefix + ' ' + lastName,
        firstName: firstName,
        prefix: prefix ? prefix : '',
        lastName: lastName,
      },
      {merge: true}
    )

    return {data: await toAuthPayload(response.user.uid, response.user.refreshToken)}
  } catch (error: any) {
    return {error: error?.message || 'Registration failed.'}
  }
}

export function requestPassword(email: string) {
  return axios.get<{result: string}>(
    'https://us-central1-s4ych33se-memorymakers.cloudfunctions.net/resetPassword?email=' + email
  )
}

export async function getUserByToken(token: string): Promise<{data: UserModel}> {
  if (!token) {
    throw new Error('UNAUTHENTICATED')
  }

  const db = getFirestore()
  const usersQuery = firebaseQuery(collection(db, '_users'), where('uid', '==', token))
  const querySnapshot = await withTimeout(getDocs(usersQuery), 20000)

  if (querySnapshot.empty) {
    throw new Error('User profile not found')
  }

  return {data: mapFirestoreUser(querySnapshot.docs[0].data())}
}
